
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, ArrowLeft } from "lucide-react";

interface PasswordRecoveryProps {
  onBackToSignIn: () => void;
}

export default function PasswordRecovery({ onBackToSignIn }: PasswordRecoveryProps) {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [error, setError] = useState("");
  
  const { requestPasswordReset } = useAuth();
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    
    try {
      const { error, success } = await requestPasswordReset(email);
      if (error) {
        setError(error.message);
      } else if (success) {
        setIsEmailSent(true);
      }
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };
  
  if (isEmailSent) {
    return (
      <div className="text-center space-y-4">
        <div className="p-4 bg-primary/10 border border-primary/30 text-primary rounded-md">
          <p>Password reset instructions sent!</p>
        </div>
        <p className="text-muted-foreground text-sm">
          We've sent a password reset link to <strong>{email}</strong>. 
          Please check your email and click the link to reset your password.
        </p>
        <Button
          type="button"
          variant="outline"
          onClick={onBackToSignIn}
          className="w-full"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Sign In
        </Button>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold font-display">Reset Password</h2>
        <p className="text-muted-foreground mt-2">
          Enter your email address and we'll send you a link to reset your password.
        </p>
      </div>
      
      {error && (
        <div className="p-3 bg-destructive/10 border border-destructive/30 text-destructive rounded-md text-sm">
          {error}
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email" className="flex gap-1 items-center">
            <Mail className="h-4 w-4" /> Email
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="h-11"
          />
        </div>
        
        <Button
          type="submit"
          className="w-full h-11 text-base"
          disabled={isLoading}
        >
          {isLoading ? "Sending..." : "Send Reset Link"}
        </Button>
      </form>
      
      <div className="text-center">
        <button
          type="button"
          onClick={onBackToSignIn}
          className="text-primary underline underline-offset-4 text-sm"
        >
          <ArrowLeft className="h-4 w-4 inline mr-1" />
          Back to Sign In
        </button>
      </div>
    </div>
  );
}
